import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Switch, Space, Button, InputNumber, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, AimOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';
import DdosTag from './DdosTag';

const { Text } = Typography;
const fmtTime = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleString() : '-');
const pct = (v) => (v === null || v === undefined ? '-' : `${Math.round(v * 100)}%`);

/** 观测点:各 yeap-agent 探测点的上报情况、失败率、是否被排除;可停用;全局探测间隔 */
const ProbeVantages = () => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [settings, setSettings] = useState({ ip_interval: 30, origin_interval: 30 });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getVantages();
        setLoading(false);
        if (r.success) {
            setItems(r.data?.items || []);
            if (r.data?.settings) setSettings(r.data.settings);
        } else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const toggle = async (row, disabled) => {
        const r = await cdnProbeService.updateVantage(row.id, disabled);
        if (r.success) load(); else message.error(r.error || t('Save failed'));
    };

    const saveSettings = async () => {
        setSaving(true);
        const r = await cdnProbeService.saveSettings(settings);
        setSaving(false);
        if (r.success) { setSettings(r.data); message.success(t('Saved')); } else message.error(r.error || t('Save failed'));
    };

    const columns = [
        {
            title: t('Vantage'), dataIndex: 'name',
            render: (n, r) => (
                <Space size={4}>
                    <span>{n}</span>
                    <Tag>{r.business_type}</Tag>
                    {!r.online && <Tag color="error">{t('Offline')}</Tag>}
                </Space>
            ),
        },
        { title: 'IP', dataIndex: 'ip', render: (ip, r) => <Space direction="vertical" size={0}><Space size={4}><Text code>{ip}</Text><DdosTag gbps={r.ddos_gbps} /></Space><Text type="secondary" style={{ fontSize: 12 }}>{r.group}</Text></Space> },
        { title: t('Agent'), dataIndex: 'agent_version', render: (v) => v || '-' },
        { title: t('Last report'), dataIndex: 'last_report_at', render: fmtTime },
        { title: t('Targets'), dataIndex: 'targets', render: (n, r) => (r.targets_v6 ? `${n} (v6 ${r.targets_v6})` : n) },
        {
            title: t('Failure rate'), dataIndex: 'fail_rate',
            render: (v) => <Text type={v > 0.5 ? 'danger' : v > 0.2 ? 'warning' : undefined}>{pct(v)}</Text>,
        },
        {
            title: t('Status'), key: 'state',
            render: (_, r) => {
                if (r.disabled) return <Tag>{t('Disabled')}</Tag>;
                if (r.excluded) return <Tooltip title={r.excluded_reason}><Tag color="warning">{t('Ignored')}</Tag></Tooltip>;
                if (!r.last_report_at) return <Tag>{t('No data')}</Tag>;
                return <Tag color="success">{t('Voting')}</Tag>;
            },
        },
        {
            title: t('Enabled'), key: 'enabled',
            render: (_, r) => <Switch size="small" checked={!r.disabled} onChange={(on) => toggle(r, !on)} />,
        },
    ];

    return (
        <Card
            title={<Space><AimOutlined />{t('Vantages')}</Space>}
            extra={
                <Space wrap>
                    <Text type="secondary">{t('IP probe interval')}</Text>
                    <InputNumber size="small" min={15} max={600} addonAfter="s" value={settings.ip_interval}
                        onChange={(v) => setSettings((s) => ({ ...s, ip_interval: v }))} style={{ width: 110 }} />
                    <Text type="secondary">{t('Origin probe interval')}</Text>
                    <InputNumber size="small" min={15} max={600} addonAfter="s" value={settings.origin_interval}
                        onChange={(v) => setSettings((s) => ({ ...s, origin_interval: v }))} style={{ width: 110 }} />
                    <Button size="small" type="primary" loading={saving} onClick={saveSettings}>{t('Save')}</Button>
                    <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            }
        >
            <Table size="small" rowKey="id" columns={columns} dataSource={items} loading={loading}
                pagination={false} scroll={{ x: 'max-content' }} />
        </Card>
    );
};

export default ProbeVantages;
